import { useCallback, useState } from 'react';
import { STORAGE_KEYS } from '../constants';
import { loadJSON, saveJSON } from '../lib/storage';
import type { EditorState, StylePreset } from '../types';

function captureStyle(state: EditorState): StylePreset['style'] {
  const { text: _mainText, ...main } = state.main;
  const { text: _subText, ...sub } = state.sub;
  return {
    main,
    sub,
    filters: { ...state.filters },
    frame: { ...state.frame },
    backgroundColor: state.backgroundColor,
  };
}

export function presetToPatch(preset: StylePreset, state: EditorState): Partial<EditorState> {
  return {
    backgroundColor: preset.style.backgroundColor,
    main: { ...preset.style.main, text: state.main.text },
    sub: { ...preset.style.sub, text: state.sub.text },
    filters: { ...preset.style.filters },
    frame: { ...preset.style.frame },
  };
}

export function usePresets() {
  const [presets, setPresets] = useState<StylePreset[]>(
    () => loadJSON<StylePreset[]>(STORAGE_KEYS.presets) ?? [],
  );

  const persist = useCallback((next: StylePreset[]) => {
    setPresets(next);
    saveJSON(STORAGE_KEYS.presets, next);
  }, []);

  const save = useCallback(
    (name: string, state: EditorState) => {
      const preset: StylePreset = {
        id: crypto.randomUUID(),
        name: name.trim() || new Date().toLocaleString(),
        style: captureStyle(state),
      };
      persist([preset, ...presets].slice(0, 30));
      return preset;
    },
    [presets, persist],
  );

  const remove = useCallback(
    (id: string) => persist(presets.filter((preset) => preset.id !== id)),
    [presets, persist],
  );

  return { presets, save, remove };
}
